import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LIFE_AREAS = [
    "Wealth",
    "Career or promotion",
    "Business success",
    "Recognition",
    "Home",
    "Travel",
    "Friendship",
    "Family",
    "The love that seemed destined for you but found her instead",
    "Lifestyle",
    "Something else",
];

const inputCls =
    "w-full rounded-xl border border-purple-500/30 bg-[#0b031e]/70 px-4 py-3 text-sm text-[#f8f7ff] placeholder:text-[#c77dff]/40 transition-[border-color,box-shadow] duration-300 focus:border-[#c77dff]/70 focus:shadow-[0_0_20px_rgba(199,125,255,0.25)] focus:outline-none";

export const IntakeOverlay = ({ onClose, onEnter }) => {
    const [form, setForm] = useState({
        first_name: "",
        email: "",
        country: "",
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "",
        sister_name: "",
        sister_relationship: "",
        diverted_area: "",
        story: "",
    });
    const [submitting, setSubmitting] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.diverted_area) {
            toast.error("Choose the area of life that feels diverted.");
            return;
        }
        setSubmitting(true);
        try {
            const res = await axios.post(`${API}/recognition/intake`, form);
            onEnter(res.data.conversation_id);
        } catch (err) {
            toast.error(err.response?.data?.detail || "The mirror clouded. Please try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            data-testid="intake-overlay"
            className="fixed inset-0 z-[90] overflow-y-auto bg-[#06010f]/90 backdrop-blur-2xl"
        >
            <div className="flex min-h-full items-center justify-center px-5 py-14">
                <motion.div
                    initial={{ opacity: 0, y: 40, scale: 0.97 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.6, delay: 0.1 }}
                    className="glass-card relative w-full max-w-2xl overflow-hidden p-8 sm:p-10"
                >
                    <div className="mirror-sheen pointer-events-none absolute inset-0 opacity-20" />
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="intake-close-button"
                        aria-label="Close"
                        className="absolute right-5 top-5 rounded-full border border-purple-500/30 p-2 text-[#e0aaff]/70 transition-colors duration-300 hover:text-[#f8f7ff]"
                    >
                        <X className="h-4 w-4" />
                    </button>

                    <p className="eyebrow mb-3">The free recognition</p>
                    <h2 className="text-glow-lilac font-display text-xl font-bold tracking-wide text-[#f8f7ff] sm:text-2xl">
                        BEFORE THE MIRROR OPENS
                    </h2>
                    <p className="mt-4 font-cormorant text-base italic leading-relaxed text-[#e0aaff]/80">
                        Give Mara the shape of the pattern. She reads every word herself.
                    </p>

                    <form onSubmit={submit} data-testid="intake-form" className="relative mt-8 grid gap-5 sm:grid-cols-2">
                        <div>
                            <label htmlFor="intake-first-name" className="eyebrow mb-2 block">First name</label>
                            <input id="intake-first-name" required value={form.first_name} onChange={set("first_name")} data-testid="intake-input-first-name" placeholder="What this realm calls you" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="intake-email" className="eyebrow mb-2 block">Email</label>
                            <input id="intake-email" required type="email" value={form.email} onChange={set("email")} data-testid="intake-input-email" placeholder="Where Mara's answer can find you" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="intake-country" className="eyebrow mb-2 block">Country</label>
                            <input id="intake-country" required value={form.country} onChange={set("country")} data-testid="intake-input-country" placeholder="Where you are in this world" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="intake-timezone" className="eyebrow mb-2 block">Time zone</label>
                            <input id="intake-timezone" value={form.timezone} onChange={set("timezone")} data-testid="intake-input-timezone" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="intake-sister-name" className="eyebrow mb-2 block">Her name or initials</label>
                            <input id="intake-sister-name" required value={form.sister_name} onChange={set("sister_name")} data-testid="intake-input-sister-name" placeholder="The possible Mirror Sister" className={inputCls} />
                        </div>
                        <div>
                            <label htmlFor="intake-relationship" className="eyebrow mb-2 block">Her relationship to you</label>
                            <input id="intake-relationship" required value={form.sister_relationship} onChange={set("sister_relationship")} data-testid="intake-input-relationship" placeholder="Sister, friend, colleague, stranger…" className={inputCls} />
                        </div>

                        <div className="sm:col-span-2">
                            <p className="eyebrow mb-3">What area of life feels diverted?</p>
                            <div className="flex flex-wrap gap-2.5">
                                {LIFE_AREAS.map((a) => (
                                    <button
                                        key={a}
                                        type="button"
                                        data-testid={`intake-area-${a.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={() => setForm((f) => ({ ...f, diverted_area: a }))}
                                        className={`rounded-full border px-4 py-2 font-cinzel text-[11px] tracking-[0.12em] transition-[border-color,box-shadow,color,background-color] duration-300 ${
                                            form.diverted_area === a
                                                ? "border-[#c77dff]/70 bg-[#5a189a]/50 text-[#f8f7ff] shadow-[0_0_18px_rgba(199,125,255,0.35)]"
                                                : "border-purple-500/25 text-[#e0aaff]/60 hover:border-[#c77dff]/50 hover:text-[#e0aaff]"
                                        }`}
                                    >
                                        {a}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="sm:col-span-2">
                            <label htmlFor="intake-story" className="eyebrow mb-2 block">What happened</label>
                            <textarea
                                id="intake-story"
                                required
                                rows={4}
                                value={form.story}
                                onChange={set("story")}
                                data-testid="intake-input-story"
                                placeholder="Tell Mara where you struggled while she succeeded, where you were punished while she was praised, and what she received that always felt destined for you…"
                                className={`${inputCls} resize-none`}
                            />
                        </div>

                        <button type="submit" disabled={submitting} data-testid="intake-submit-button" className="btn-glow relative mt-2 w-full disabled:opacity-70 sm:col-span-2">
                            <span className="mirror-sheen absolute inset-0 animate-shimmer-line" />
                            <span className="relative">{submitting ? "OPENING…" : "ENTER MARA'S BACK ROOM"}</span>
                        </button>
                    </form>
                </motion.div>
            </div>
        </motion.div>
    );
};
